import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRazorpayPayment } from "../../hooks/useRazorpayPayment";
import { toast } from "react-toastify";
import PaymentProcessingOverlay from "../../components/PaymentProcessingOverlay";

// ✅ Simple Cart Icon (SVG)
const CartIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 7h12L17 13M9 21a1 1 0 110-2 1 1 0 010 2zm8 0a1 1 0 110-2 1 1 0 010 2z"
    />
  </svg>
);

// ✅ Simple Spinner Icon (SVG)
const SpinnerIcon = ({ className }) => (
  <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    ></path>
  </svg>
);

const PremadeCombos = ({
  subjectData,
  branchData,
  year,
  branch,
  semester,
  subject,
}) => {
  const { initiatePayment, verifying } = useRazorpayPayment();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [subjectLoading, setSubjectLoading] = React.useState(false);
  const [branchLoading, setBranchLoading] = React.useState(false);

  const handleViewSubjectDemo = () => {
    window.open(
      "https://drive.google.com/drive/folders/17fDmMzHlV5Aje-5yCBWaqtS3HyaeBJ3Q?usp=drive_link",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleViewBranchDemo = () => {
    window.open(
      "https://drive.google.com/drive/folders/17fDmMzHlV5Aje-5yCBWaqtS3HyaeBJ3Q?usp=drive_link",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleSubjectPurchase = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to purchase notes");
      navigate("/login");
      return;
    }
    if (!subjectData) {
      toast.warning("Please search for notes first");
      return;
    }

    setSubjectLoading(true);
    try {
      await initiatePayment({
        amount: subjectData.price,
        unitId: [],
        subjectId: [subjectData._id],
        branchId: [],
      });
    } finally {
      setSubjectLoading(false);
    }
  };

  const handleBranchPurchase = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to purchase notes");
      navigate("/login");
      return;
    }
    if (!branchData) {
      toast.warning("Please search for notes first");
      return;
    }

    setBranchLoading(true);
    try {
      await initiatePayment({
        amount: branchData.price,
        unitId: [],
        subjectId: [],
        branchId: [branchData._id],
      });
    } finally {
      setBranchLoading(false);
    }
  };

  // Temporarily hiding this for semester 2 for a shorter period of time.
  // The code is preserved below, just conditionally rendered to "comment it off" for semester 2.
  // Conditional rendering for semester 2 block was here but is now removed to show packages.

  return (
    <>
      <PaymentProcessingOverlay visible={verifying} />
      <div className="grid sm:grid-cols-2 gap-4">
      <div className="bg-slate-900/60 border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
        <div>
          <div className="text-xs text-gray-400 uppercase tracking-wide">
            Subject Combo
          </div>
          <h4 className="mt-1 font-semibold text-white truncate">
            {subjectData?.title || "No subject selected"}
          </h4>
          <h6 className="mt-2 text-sm text-gray-300">
            Complete subject package
          </h6>
          <p className="mt-2 text-sm text-gray-300">
            Complete PYQs for all units — best for quick and focused study.
          </p>
        </div>

        <div className="mt-4">
          <div className="text-lg font-bold text-white mb-3">
            ₹{subjectData?.price ?? "-"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewSubjectDemo}
              disabled={!subjectData}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                !subjectData
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              View Demo
            </button>
            <button
              onClick={handleSubjectPurchase}
              disabled={!subjectData || subjectLoading}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition ${
                !subjectData || subjectLoading
                  ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-500"
              }`}
            >
              {subjectLoading ? (
                <SpinnerIcon className="w-4 h-4" />
              ) : (
                <CartIcon className="w-4 h-4" />
              )}
              {subjectLoading
                ? "Processing"
                : subjectData
                  ? "Buy"
                  : "Search first"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/60 border border-gray-800 rounded-xl p-4 flex flex-col justify-between">
        <div>
          <div className="text-xs text-gray-400 uppercase tracking-wide">
            Branch Combo
          </div>
          <h4 className="mt-1 font-semibold text-white truncate">
            {`${branchData?.year} ${branchData?.branch}` ||
              "No branch selected"}
          </h4>
          <h6 className="mt-2 text-sm text-gray-300">
            Complete branch package
          </h6>
          <p className="mt-2 text-sm text-gray-300">
            Complete PYQs for all subjects — best for quick and focused study.
          </p>
        </div>

        <div className="mt-4">
          <div className="text-lg font-bold text-white mb-3">
            ₹{branchData?.price ?? "-"}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleViewBranchDemo}
              disabled={!branchData}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
                !branchData
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-gray-700 text-white hover:bg-gray-600"
              }`}
            >
              View Demo
            </button>
            <button
              onClick={handleBranchPurchase}
              disabled={!branchData || branchLoading}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition ${
                !branchData || branchLoading
                  ? "bg-gray-700 text-gray-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-95"
              }`}
            >
              {branchLoading ? (
                <SpinnerIcon className="w-4 h-4" />
              ) : (
                <CartIcon className="w-4 h-4" />
              )}
              {branchLoading
                ? "Processing"
                : branchData
                  ? "Buy"
                  : "Search first"}
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default PremadeCombos;
